import React from 'react';
import clsx from 'clsx';
import { ChevronLeftIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';

const FILL_HEIGHT_CLASS = 'flex min-h-0 flex-1 flex-col';

export const PageTitle = (
  props: React.PropsWithChildren<{
    title: string | JSX.Element;
    /**
     * Small line above the title naming the section the page sits in. Pages
     * whose tabs live in the sidebar use it for the parent page, so the heading
     * can be the tab itself without losing where you are.
     */
    eyebrow?: string;
    /** Makes the eyebrow a way back to the section it names. */
    eyebrowHref?: string;
    description?: string | JSX.Element;
    badge?: JSX.Element;
    menu?: JSX.Element;
    /**
     * Fills a parent of fixed height as flex columns, so one child with `min-h-0` and its own
     * overflow can scroll while the rest stays put.
     */
    fillHeight?: boolean;
  }>
) => (
  <div className={props.fillHeight ? FILL_HEIGHT_CLASS : undefined}>
    <div className="lg:flex lg:items-start lg:justify-between">
      <div className="min-w-0 flex-1">
        {props.eyebrow &&
          (props.eyebrowHref ? (
            <Link
              to={props.eyebrowHref}
              className="-ml-1 mb-1 inline-flex items-center gap-0.5 rounded text-sm font-medium text-neutral-500 hover:text-neutral-800 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 dark:text-neutral-400 dark:hover:text-neutral-200"
            >
              <ChevronLeftIcon className="size-4" aria-hidden="true" />
              {props.eyebrow}
            </Link>
          ) : (
            <div className="mb-1 text-sm font-medium text-neutral-500 dark:text-neutral-400">
              {props.eyebrow}
            </div>
          ))}
        <h1 className="m-0 dark:text-white">{props.title}</h1>
        {props.badge && <div className="pt-2">{props.badge}</div>}
        {props.description && (
          <p className="text-base mt-2 text-neutral-500 dark:text-neutral-400">
            {props.description}
          </p>
        )}
      </div>
      {props.menu && <div className="mt-5 flex lg:ml-4 lg:mt-0">{props.menu}</div>}
    </div>
    <div className={clsx('mt-8', props.fillHeight && FILL_HEIGHT_CLASS)}>
      {props.children}
    </div>
  </div>
);

export const PageSection = (
  props: React.PropsWithChildren<{ title: string; description?: string }>
) => (
  <div style={{ padding: '24px 0px 0px 0px' }}>
    <h2 className="mb-0 font-medium">{props.title}</h2>
    {props.description && (
      <p className="text-sm mt-1 text-neutral-700 dark:text-neutral-400">{props.description}</p>
    )}
    <div className="mt-4">{props.children}</div>
  </div>
);
